import { type ChangeEvent, type DragEvent, useMemo, useRef, useState } from 'react';
import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import type { CreatorProfile as Creator, ContractTemplate } from '@/types/portal';
import { formatMoney } from '@/utils/format';
import {
  contractTokens,
  resolveContract,
  buildContractValues,
  defaultContractBody,
  tokensInBody,
} from '@/utils/contract';
import type { ContractDraft } from '../types';
import ContractTemplates from './ContractTemplates';
import { formatLabels, matchedFormats } from '../helpers';

interface ContractStepProps {
  draft: ContractDraft;
  onDraft: (patch: Partial<ContractDraft>) => void;
  creators: Creator[];
  contentTypes: string[];
  customContentType: string;
  rates: Record<string, number>;
  brand: string;
  campaignName: string;
  deliverables: string;
  currency: string;
  startDate: string;
  endDate: string;
  onBack: () => void;
  onNext: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

const acceptedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md'];
const maxUploadBytes = 10 * 1024 * 1024;

export default function ContractStep({
  draft,
  onDraft,
  creators,
  contentTypes,
  customContentType,
  rates,
  brand,
  campaignName,
  deliverables,
  currency,
  startDate,
  endDate,
  onBack,
  onNext,
}: ContractStepProps) {
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewId, setPreviewId] = useState<string>(creators[0]?.id ?? '');
  const [dragging, setDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const usedTokens = useMemo(() => tokensInBody(draft.body), [draft.body]);
  const usedSet = useMemo(() => new Set(usedTokens.map((item) => item.token)), [usedTokens]);

  const previewCreator = creators.find((creator) => creator.id === previewId) ?? creators[0] ?? null;

  const preview = useMemo(() => {
    if (!previewCreator) return draft.body;
    const custom = customContentType.trim();
    const contentLabel =
      [formatLabels(matchedFormats(previewCreator, contentTypes)), custom].filter(Boolean).join(', ') ||
      'the agreed content formats';
    const values = buildContractValues({
      creatorName: previewCreator.name,
      handle: previewCreator.handle,
      brand: brand || 'the Brand',
      campaign: campaignName || 'the campaign',
      currency,
      amount: rates[previewCreator.id] ?? previewCreator.rate ?? 0,
      deliverables: deliverables.trim() || 'the agreed deliverables',
      contentTypes: contentLabel,
      startDate,
      endDate,
    });
    return resolveContract(draft.body, values);
  }, [
    previewCreator,
    contentTypes,
    customContentType,
    deliverables,
    rates,
    brand,
    campaignName,
    currency,
    startDate,
    endDate,
    draft.body,
  ]);

  const applyFile = (file: File) => {
    setUploadError(null);
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (!acceptedExtensions.includes(ext)) {
      setUploadError('Unsupported file — upload a PDF, Word document or text file.');
      return;
    }
    if (file.size > maxUploadBytes) {
      setUploadError('That document is over 10 MB. Upload a smaller file.');
      return;
    }
    const fileType = ext.replace('.', '').toUpperCase();
    const meta = { fileName: file.name, fileType, fileSize: formatFileSize(file.size) };
    // Plain-text agreements can be read straight into the editable contract body.
    if (ext === '.txt' || ext === '.md') {
      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === 'string' ? reader.result : '';
        onDraft({ ...meta, body: text.trim() ? text : defaultContractBody });
      };
      reader.readAsText(file);
    } else {
      onDraft(meta);
    }
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) applyFile(file);
    event.target.value = '';
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) applyFile(file);
  };

  const insertToken = (token: string) => {
    const el = bodyRef.current;
    if (!el) {
      onDraft({ body: `${draft.body} ${token}` });
      return;
    }
    const start = el.selectionStart ?? draft.body.length;
    const end = el.selectionEnd ?? draft.body.length;
    const next = draft.body.slice(0, start) + token + draft.body.slice(end);
    onDraft({ body: next });
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = start + token.length;
      el.selectionEnd = start + token.length;
    });
  };

  const applyTemplate = (template: ContractTemplate) => {
    onDraft({
      fileName: template.fileName,
      fileType: template.fileType,
      fileSize: template.fileSize,
      body: template.body,
    });
    setUploadError(null);
  };

  const fileReady = draft.fileName.trim().length > 0;

  return (
    <div className="space-y-5">
      <ContractTemplates
        draft={draft}
        defaultName={campaignName ? `${campaignName} agreement` : 'Creator agreement'}
        sourceCampaign={campaignName}
        onApply={applyTemplate}
      />

      {/* Upload */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">
              Contract for hired creators
            </h2>
            <p className="mt-0.5 text-xs text-foreground-500">
              Upload the agreement you share with creators, or skip it and use the contract text
              below. Every creator signs the same template, filled with their own details.
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-secondary-200 bg-secondary-50 px-2.5 py-1 text-[11px] font-label text-secondary-900">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-shield-check-line" />
            </span>
            E-signature required
          </span>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt,.md"
          className="hidden"
          onChange={onInputChange}
        />

        {fileReady ? (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-background-200 bg-background-100 p-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700 text-lg">
              <i className="ri-file-text-line" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground-900">{draft.fileName}</p>
              <p className="text-xs text-foreground-500">
                {draft.fileType} · {draft.fileSize} · {usedTokens.length} dynamic field
                {usedTokens.length === 1 ? '' : 's'} detected
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                icon="ri-refresh-line"
                onClick={() => fileInputRef.current?.click()}
              >
                Replace
              </Button>
              <button
                type="button"
                onClick={() => {
                  onDraft({ fileName: '', fileType: '', fileSize: '' });
                  setUploadError(null);
                }}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-label text-foreground-600 hover:bg-background-100 hover:text-primary-700"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-delete-bin-line" />
                </span>
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setDragging(false);
            }}
            onDrop={onDrop}
            className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed px-6 py-10 text-center transition-colors ${
              dragging
                ? 'border-primary-400 bg-primary-50'
                : 'border-background-300 bg-background-100 hover:border-primary-300 hover:bg-primary-50'
            }`}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-xl text-foreground-500">
              <i className="ri-upload-cloud-2-line" />
            </span>
            <span className="mt-3 text-sm font-medium text-foreground-800">
              Upload contract template
            </span>
            <span className="mt-1 text-xs text-foreground-500">
              PDF, Word or text document up to 10 MB — drop a file or click to browse
            </span>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-50 px-2.5 py-1 text-[11px] font-label text-foreground-500">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-file-text-line" />
              </span>
              Optional — without a file, the contract text below is used
            </span>
          </div>
        )}

        {uploadError ? (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-primary-700">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-error-warning-line" />
            </span>
            {uploadError}
          </p>
        ) : null}
      </div>

      {/* Dynamic fields */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">
              Dynamic fields
            </h2>
            <p className="mt-0.5 text-xs text-foreground-500">
              Drop a field anywhere in the contract text below and CPaaS fills it in per creator.
            </p>
          </div>
          <span className="shrink-0 text-[11px] font-label text-accent-700">
            {usedTokens.length}/{contractTokens.length} in use
          </span>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {contractTokens.map((item) => {
            const active = usedSet.has(item.token);
            return (
              <button
                key={item.token}
                type="button"
                title={item.description}
                onClick={() => insertToken(item.token)}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-label transition-colors ${
                  active
                    ? 'border-accent-300 bg-accent-50 text-accent-800'
                    : 'border-background-300 bg-background-50 text-foreground-600 hover:border-secondary-300'
                }`}
              >
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className={active ? 'ri-check-line' : 'ri-add-line'} />
                </span>
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          <label
            htmlFor="contract-body"
            className="text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400"
          >
            Contract text
          </label>
          <textarea
            id="contract-body"
            ref={bodyRef}
            value={draft.body}
            onChange={(event) => onDraft({ body: event.target.value })}
            rows={10}
            className="mt-1.5 w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-xs leading-relaxed text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
          />
        </div>
      </div>

      {/* Preview */}
      <div className="rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-5 py-3">
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            Preview — filled for each creator
          </h3>
          {creators.length > 0 ? (
            <div className="flex max-w-full flex-wrap items-center gap-1.5">
              {creators.map((creator) => (
                <button
                  key={creator.id}
                  type="button"
                  onClick={() => setPreviewId(creator.id)}
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-label transition-colors ${
                    previewId === creator.id
                      ? 'border-primary-400 bg-primary-50 text-primary-800'
                      : 'border-background-300 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <Avatar src={creator.avatar} alt={creator.name} size="xs" />
                  {creator.name.split(' ')[0]}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <div className="max-h-[340px] overflow-y-auto px-5 py-4">
          {previewCreator ? (
            <p className="mb-3 flex items-center gap-2 text-xs text-foreground-500">
              <span className="w-4 h-4 flex items-center justify-center text-accent-600">
                <i className="ri-magic-line" />
              </span>
              Showing {previewCreator.name}'s contract ·{' '}
              {formatMoney(rates[previewCreator.id] ?? previewCreator.rate ?? 0, currency)}
            </p>
          ) : null}
          <pre className="whitespace-pre-wrap font-body text-xs leading-relaxed text-foreground-700">
            {preview}
          </pre>
        </div>
      </div>

      {/* Hire gating policy */}
      <div className="flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50">
          <i className="ri-lock-2-line" />
        </span>
        <div>
          <h3 className="font-heading text-sm font-semibold text-secondary-900">
            Signing blocks activation
          </h3>
          <p className="mt-0.5 text-xs leading-relaxed text-secondary-900">
            A creator cannot be fully hired until they digitally sign this contract. Until then they
            stay on the shortlist, their fee is not committed and their content stays locked.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" icon="ri-arrow-left-line" onClick={onBack}>
          Back to shortlist
        </Button>
        <Button icon="ri-arrow-right-line" onClick={onNext}>
          Continue to invite
        </Button>
      </div>
    </div>
  );
}